// packages/web-ui/src/components/slash-palette/parameter-form.tsx
//
// Story W2-S7 AC #7-#8 — parameter-form widget shared by `<SlashMenu>` and
// `<GlobalPalette>`. Renders an input per declared parameter (string ↦ text
// input; enum ↦ select), a live preview line, and Run/Cancel buttons.

import type { ChangeEvent, FormEvent, JSX } from 'react';
import { useCallback, useEffect, useRef } from 'react';
import { ChevronRight, X } from 'lucide-react';
import { buildPreview, type SlashCommand } from './commands.js';

export interface ParameterFormProps {
  command: SlashCommand;
  /** Live parameter values; controlled by the parent. */
  values: Record<string, unknown>;
  onChange: (name: string, value: unknown) => void;
  onRun: () => void;
  onCancel: () => void;
  /** Optional autofocus target — defaults to the first parameter input. */
  autoFocusFirst?: boolean;
}

export function ParameterForm(props: ParameterFormProps): JSX.Element {
  const { command, values, onChange, onRun, onCancel, autoFocusFirst = true } = props;
  const firstInputRef = useRef<HTMLInputElement | HTMLSelectElement | null>(null);

  useEffect(() => {
    if (autoFocusFirst && firstInputRef.current !== null) {
      firstInputRef.current.focus();
    }
  }, [autoFocusFirst, command.id]);

  const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    onRun();
  };

  const setFirstInputRef = useCallback(
    (el: HTMLInputElement | HTMLSelectElement | null): void => {
      firstInputRef.current = el;
    },
    [],
  );

  return (
    <form
      data-testid="slash-parameter-form"
      data-command-id={command.id}
      onSubmit={handleSubmit}
      className="flex flex-col gap-2 px-3 py-2"
    >
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-wide text-taupe-700">
        <ChevronRight aria-hidden="true" className="h-3 w-3" />
        <span>{command.label}</span>
      </div>

      {command.parameters.map((param, idx) => {
        const refCallback = idx === 0 ? setFirstInputRef : undefined;
        const handleStringChange = (event: ChangeEvent<HTMLInputElement>): void => {
          onChange(param.name, event.target.value);
        };
        const handleEnumChange = (event: ChangeEvent<HTMLSelectElement>): void => {
          onChange(param.name, event.target.value);
        };

        return (
          <label
            key={param.name}
            data-testid={`slash-param-${param.name}`}
            className="flex flex-col gap-1 text-[12px] text-taupe-900"
          >
            <span className="font-medium">{param.name}</span>
            {param.type === 'enum' && param.enumValues !== undefined ? (
              <select
                ref={refCallback}
                data-testid={`slash-param-${param.name}-input`}
                value={String(values[param.name] ?? '')}
                onChange={handleEnumChange}
                className="text-fg border-taupe-500 bg-cream-50 focus:border-audi-red rounded-none border px-2 py-1 focus:outline-none focus:ring-1 focus:ring-audi-red"
              >
                {param.enumValues.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            ) : (
              <input
                ref={refCallback}
                data-testid={`slash-param-${param.name}-input`}
                type="text"
                placeholder={param.placeholder ?? ''}
                value={String(values[param.name] ?? '')}
                onChange={handleStringChange}
                className="text-fg border-taupe-500 bg-cream-50 focus:border-audi-red rounded-none border px-2 py-1 focus:outline-none focus:ring-1 focus:ring-audi-red"
              />
            )}
          </label>
        );
      })}

      <pre
        data-testid="slash-preview"
        className="border-taupe-300 bg-cream-100 text-fg overflow-x-auto rounded-none border px-2 py-1 font-mono text-[11px]"
      >
        {buildPreview(command, values)}
      </pre>

      <div className="flex justify-end gap-2 pt-1">
        <button
          type="button"
          data-testid="slash-cancel"
          onClick={onCancel}
          className="border-taupe-500 hover:border-taupe-900 text-fg hover:bg-cream-100 inline-flex items-center gap-1 rounded-none border px-3 py-1 text-[11px] font-semibold uppercase tracking-wide"
        >
          <X aria-hidden="true" className="h-3 w-3" />
          Cancel
        </button>
        <button
          type="submit"
          data-testid="slash-run"
          className="bg-taupe-900 hover:bg-audi-red inline-flex items-center gap-1 rounded-none px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-white"
        >
          Run
        </button>
      </div>
    </form>
  );
}
